import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdatabaseAuto,
  IdeleteOneResponse,
  IfilterProps, Iuser,
  IuserIpAnalytics
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { AuthClient } from '../auth-client';

/**
 * Represents IP reputation and status metadata tracked for a user.
 *
 * Includes whitelisted (green), suspicious (red), and unverified IP lists,
 * plus a blocking state tied to login attempts.
 *
 * @public
 */
export interface IuserIp
  extends IdatabaseAuto {

  /** Unique identifier of this user-IP record. */
  _id: string;

  /** The user this IP metadata belongs to. */
  user: Iuser;

  /** Known safe IP addresses for the user. */
  greenIps: string[];

  /** Known risky or flagged IP addresses for the user. */
  redIps: string[];

  /** IP addresses seen but not yet verified. */
  unverifiedIps: string[];

  /**
   * Block state details.
   * - status: Whether the user is currently blocked.
   * - loginAttemptRef: Reference to the triggering attempt/event.
   * - timesBlocked: Number of times the user has been blocked.
   */
  blocked: {
      status: boolean;
      loginAttemptRef: string;
      timesBlocked: number;
    };
}

/**
 * Client-side model for interacting with the User IP API.
 *
 * Provides helpers to fetch, filter, update, and delete user IP records,
 * as well as to retrieve analytics for reporting/monitoring.
 *
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link UserIp} class when invoking client operations.
 *
 * @example
 * ```typescript
 * const result = await UserIp.getAll();
 *
 * const result = await UserIp.filterAll({
 *   // filter criteria
 * });
 *
 * const result = await UserIp.bulkDelete({
 *   // delete criteria
 * });
 *
 * const userIp = await UserIp.getOne('resourceId');
 *
 * const result = await UserIp.remove();
 * ```
 */

export class UserIp
  extends DatabaseAuto {
  /** Base path for user IP routes. */
  private static readonly baseUrl = '/user-ip';

  /** The user this IP metadata belongs to. */
  readonly user: IuserIp['user'];

  /** Known safe IP addresses for the user. */
  readonly greenIps: IuserIp['greenIps'];

  /** Known risky or flagged IP addresses for the user. */
  readonly redIps: IuserIp['redIps'];

  /** IP addresses seen but not yet verified. */
  readonly unverifiedIps: IuserIp['unverifiedIps'];

  /** Current block state details. */
  readonly blocked: IuserIp['blocked'];

  /**
   * Constructs an instance of {@link UserIp}.
   *
   * @param data - The user IP data used to initialize the instance.
   */
  constructor(data: IuserIp) {
    super(data);

    this.user = data.user;
    this.greenIps = data.greenIps;
    this.redIps = data.redIps;
    this.unverifiedIps = data.unverifiedIps;
    this.blocked = data.blocked;
  }

  /**
   * Retrieves analytics related to user IP data.
   *
   * @param filter - Filter criteria to compute analytics on a subset of records.
   * @returns A promise that resolves to the computed user IP analytics.
   */
  static getUserIpAnalytics(filter: IfilterProps) {
    const observer$ = AuthClient.ehttp
      .makePost<IuserIpAnalytics>(`${UserIp.baseUrl}/analytics`, filter);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all user IPs with pagination support.
   *
   * @param offset - The starting point for pagination. Defaults to `0`.
   * @param limit - The number of items to retrieve. Defaults to `20`.
   * @returns A promise that resolves to the total count and an array of
   * {@link UserIp} instances.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = AuthClient.ehttp
      .makeGet<IdataArrayResponse<IuserIp>>(`${UserIp.baseUrl}?${query}`);
    const userIps = await lastValueFrom(observer$);

    return {
      count: userIps.count,
      dataArr: userIps.data.map((val) => new UserIp(val))
    };
  }

  /**
   * Filters user IPs based on the provided filter properties.
   *
   * @param filter - The filter criteria for retrieving user IP records.
   * @returns A promise that resolves to the total count and an array of
   * {@link UserIp} instances matching the filter.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = AuthClient.ehttp
      .makePost<IdataArrayResponse<IuserIp>>(`${UserIp.baseUrl}/filter`, filter);
    const userIps = await lastValueFrom(observer$);

    return {
      count: userIps.count,
      dataArr: userIps.data.map((val) => new UserIp(val))
    };
  }

  /**
   * Retrieves a single user IP record by resource identifier.
   *
   * @param resourceId - The unique identifier for the user IP record to retrieve.
   * @returns A promise that resolves to the {@link UserIp} instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = AuthClient.ehttp
      .makeGet<IuserIp>(`${UserIp.baseUrl}/${resourceId}`);
    const userip = await lastValueFrom(observer$);

    return new UserIp(userip);
  }

  /**
   * Removes multiple user IP records based on the provided criteria.
   *
   * @param payload - The criteria for deleting multiple user IPs.
   * @returns A promise that resolves to the bulk delete operation result.
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = AuthClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${UserIp.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current user IP record with the provided data.
   *
   * @param payload - The fields to update on this user IP record.
   * @returns A promise that resolves to the updated {@link UserIp} instance.
   */
  async update(payload: Partial<IuserIp>) {
    const observer$ = AuthClient.ehttp
      .makePut<IuserIp>(`${UserIp.baseUrl}/${this._id}`, payload);

    return new UserIp(await lastValueFrom(observer$));
  }


  /**
   * Removes the current user IP record.
   *
   * @returns A promise that resolves to the delete operation result, or `null`.
   */
  remove() {
    const observer$ = AuthClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${UserIp.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
